import React from "react";
import {createRoot} from 'react-dom/client';
import {translator} from "core/localization/localization";
import {Grid} from "controls/react/form";
import {Window} from 'controls/react/kendoWrappers/window';
import Settings from 'settings';
import Cookies from 'core/cookies';
import Api from 'tools/api';
import WidgetInfoContainer from "controls/react/widgetInfoContainer";
import {MetricLegendContainer} from './metricLegendContainer'
import RemoteEventsManager from 'core/remoteEventsManager';
import {ApplicationState} from 'framework/applicationState';

import './metricTableDataWindow.less'
import { extractQualifierSubscriptionFields } from "areas/service-boards/widgets/customMetrics/metricSelector";
import {
	areTheSame,
	renderIntoLegacy, TimePeriod,
	timePeriodToUrl
} from "./react/form/timePeriodSelector";
import {newGuid} from "tools/guid";
import {formatNumber} from "tools/helpers/math";
import {TimePeriodType} from "controls/react/form/timePeriodType";
import {Utils} from "tools";
import {DownloadOutlined} from "@ant-design/icons";
import {WidgetToolbox, WidgetToolboxItem} from "areas/service-boards/widgetTollbox";

let i = translator({
  "Metrics data": {
    "no": "Metrikk data",
    "en": "Metric data"
  }
});

const b = require('b_').with('metric-table-data-window');

export class MetricTableDataWindow extends React.PureComponent {
	timePeriod = {period: TimePeriodType.LastHour}
	timePeriodSetValue = (value) => {}

	gridDataSource;
	constructor(props) {
		super(props);
		this.state = {
			gridShown: true
		};
		this.metricId = this.props.metricId;
		this.windowActions = this.props.actions || ['Close']
		this.timePeriod = this.initialTimeSelectorValue ?? {period: TimePeriodType.LastHour};
	}

	get initialTimeSelectorValue() {
		if (!this.props.defaultTimeSelectorValue)
			return

		if (this.props.defaultTimeSelectorValue.period) {
			return this.props.defaultTimeSelectorValue
		}

		return {period: this.props.defaultTimeSelectorValue}
	}

	get instanceConfiguration() {
		return this.timePeriod;
	}

	updateTimePeriod = (period, periodZoomed) => {
		const effectiveTimePeriod = periodZoomed ?? period
		if(areTheSame(this.timePeriod, effectiveTimePeriod))
			return
		this.timePeriodSetValue(effectiveTimePeriod);
		this.onTimePeriodChange(effectiveTimePeriod);
	}

	render() {
		let content;
		if (this.props.metricId && this.state.gridShown) {
			content = <div className={b('content')}>
			<Grid
				dataSource={this.gridDataSource}
				columns={this.getGridColumns()}
				containerClass={b('grid-container')}
				scrollable={{virtual: true}}
				sortable={{
					mode: "multiple",
					allowUnsort: true
				}}
				dynamicFit={true}
				serverSorting={true}
				serverPaging={true}
				serverFiltering={true}
				skipSelectorColumn={true}
				noRecords={{template: i('No data')}}
				ref={grid => this.grid = grid}
			>
			</Grid>
			<MetricLegendContainer className={b('legend')} data={this.state.legendData} timeZone={this.props.timeZone}/>
			</div>
		} else {
			content = <div className="no-data-placeholder"><span>{i('No data')}</span></div>;
		}

		if(this.props.window != true)
			return content;

		if (this.props.context) {
			let openedTableWindows = this.props.context.openedTableDataWindowIds || [];
			let index = openedTableWindows.indexOf(this.metricId);
			this.isWindowOpened = index !== -1 ? true : false;
		} else {
			this.isWindowOpened = true;
		}

		let windowDimensions = this.getWindowDimensions();

		return (
			<div>
				{this.isWindowOpened && <Window height={windowDimensions.windowHeight} flexContent={true}
						width={windowDimensions.windowWidth}
						title={this.props.title}
						top={windowDimensions.windowTop}
						left={windowDimensions.windowLeft}
						minHeight={100}
						minWidth={600}
						actions={this.windowActions}
						className={b()}
						ref={window => this.window = window}
						onClose={() => this.hideTableDataWindow()}>
					{(!this.metricId || !!this.state.legendData) && content}
				</Window>}
			</div>
		)
	}

	async componentDidMount() {
		this.gridDataSource = this.getGridDataSource();
		if (this.metricId) {
			await this.renderInfoContainer();
		}

		if (!this.props.window || this.window) {
			this.renderTimeSelector();
		}
		if(this.window) {
			let htmlClass = 'table' + this.metricId;
			this.window.window.wrapper.addClass(htmlClass);
			if (this.props.cacheWindow) {
				this.props.cacheWindow(this.window.window, this.props.metricId);
			}
		}
		this.subscribe();
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (prevProps.defaultTimeSelectorValue != this.props.defaultTimeSelectorValue) {
			this.renderTimeSelector();
		}
		if(prevProps.timeSelectorAppearance != this.props.timeSelectorAppearance) {
			this.renderTimeSelector();
		}
	}

	getWindowDimensions() {
		let windowWidth = this.props.width || 600;
		let windowHeight = this.getContentHeight() + 62;
		let windowTop = this.props.top || 150;
		let windowLeft = this.props.left || 350;

		if (!this.props.skipDimensionsAdjust) {
			let maxLeft = 0;
			let maxTop = 0;
			$('.k-widget.k-window').each(function() {
				let currentTop = $(this).offset().top;
				let currentLeft = $(this).offset().left;
				if (currentTop >= maxTop) {
					maxTop = currentTop;
					maxLeft = currentLeft;
					if (currentLeft > maxLeft) {
						maxLeft = currentLeft;
					}
				}
			});

			if (maxLeft && maxTop) {
				windowTop = maxTop;
				windowLeft = maxLeft + windowWidth;
				if (windowLeft + windowWidth > window.innerWidth) {
					windowLeft = 350;
					windowTop = maxTop + windowHeight;
					if (windowTop + windowHeight > window.innerHeight) {
						windowTop = window.innerHeight - windowHeight;
					}
				}
			}
		}

		return {
			windowTop,
			windowLeft,
			windowHeight,
			windowWidth
		}
	}

	getContentHeight(){
		return this.props.height || 220;
	}

	getConversion() {
		const {conversion} = this.props;
		if(!conversion) {
			return {}
		}
		const result = {
			conversionUnit: conversion.unit
		}
		if(conversion.unit === 'CUSTOM') {
			result.conversion = conversion.formula
			result.customUnit = conversion.unitLabel
		}
		return result
	}

	dataSource = null
	getGridDataSource() {
		if(this.dataSource == null) {
			let periodTranslationObj = {
				'LASTHOUR': 'LASTDAY',
				'LASTDAY': 'LAST7DAYS',
				'LAST7DAYS': 'LAST30DAYS'
			};

			this.dataSourceUrl = this.getDataSourceUrl();
			const oThis = this;
			this.dataSource = new kendo.ceeview.DataSource({
				transport: {
					read: (options) => {
						$.ajax({
							url: this.dataSourceUrl,
							contentType: "application/json; charset=utf-8",
							type: "POST",
							dataType: "json",
							cache: false,
							data: kendo.stringify({
								...this.getConversion(),
								take: options.data.take,
								skip: options.data.skip,
								page: options.data.page,
								pageSize: options.data.pageSize,
								sort: options.data.sort
							}),
							processData: false,
							success(result) {
								options.success(result);

								oThis.setState({
									unitType: result.unitType
								}, () => {
									oThis.subscribe()
								})
							},
							error: () => {
								this.setState({
									gridShown: false
								});
							}
						});
					},
				},
				pageSize: 200,
				serverPaging: true,
				serverSorting: true,
				schema: {
					data: $.proxy(function (response) {
						if (response.items && !response.items.length && !this.doNotTranslatePeriod) {
							let newTimeSelector = periodTranslationObj[this.currentTimeSelector];
							if (newTimeSelector) {
								this.timePeriod = {period: newTimeSelector};
								this.timePeriodSetValue(newTimeSelector)
								this.dataSourceUrl = this.getDataSourceUrl();
								this.grid.kendoGrid.dataSource.read();
							}
						}
						return response.items || [];
					}, this),
					total: function (response) {
						this.visibleItems = response.visible;
						this.totalItems = response.total;
						return response.visible;
					}
				},
				sort: {
					field: 't',
					dir: 'desc'
				},
				useRanges: true,
				virtual: true
			});
		}

		return this.dataSource
	}

	columns = null
	getGridColumns() {
		if (this.columns == null) {
			this.columns = [{
				field: 't',
				title: lang.TIME,
				template: '${Renderer.browserDateRenderer(t, "datetime", null, "' + this.props.timeZone + '")}',
				width: 190,
				sortable: true,
			}, {
				field: 'v',
				title: lang.VALUE,
				template: (data) => {
					if (data.err) {
						if (data.err[0] !== 0) {
							return '<span class="cw_label cw_label_error">' + lang.ERROR + '</span>';
						}
					}
					if (data.e) {
						if (data.e[0] !== 0) {
							return '<span class="cw_label cw_label_error">' + lang.ERROR + '</span>';
						}
					}
					if (data.v === null) {
						return lang.NA;
					} else {
						const decimals = this.props.conversion?.decimals
						const number = (decimals || decimals === 0) ? formatNumber(data.v, decimals) : data.v
						return `${number} ${data.u}`
					}
				},
				width: 100
			}];
		}
		return this.columns
	}

	async renderInfoContainer() {
		let url = `${Settings.serverPath}accounts/${Cookies.CeesoftCurrentAccountId}/metrics/registeredMetrics`;
		let result = await Api.fetchPost(url, [this.metricId]);

		return new Promise(resolve => {
			this.setState({
				legendData: result[0]
			}, () => {
				resolve()
			})
		})
	}

	onNewTimeSelectorChanged = (value) => {
		const defaultPeriod = typeof this.props.defaultTimeSelectorValue == 'string'
			? {period: this.props.defaultTimeSelectorValue}
			: this.props.defaultTimeSelectorValue;

		this.timePeriod = Object.assign({}, defaultPeriod ?? {}, value);
		this.dataSourceUrl = this.getDataSourceUrl();
		this.grid?.kendoGrid?.dataSource.read();
		if (this.props.onWidgetPeriodChanged) {
			this.props.onWidgetPeriodChanged?.(this.timePeriod)
		} else {
			this.renderTimeSelector();
		}
	}

	getDataSourceUrl = () => {
		const urlParams = timePeriodToUrl(this.timePeriod);
		return `${Settings.serverPath}sessions/${ApplicationState.apiToken}/metrics/${this.metricId}?${urlParams}`;
	}

	renderTimeSelector() {
		this.target = this.window?.window.wrapper ?? $('.metric-table-widget');
		if(!this.timePeriodRoot) {
			const container = this.props.timeSelectorContainer ? $(this.props.timeSelectorContainer) : this.target.find('.k-window-actions');
			container.prepend('<div class="toolbar-buttons-set"></div>');
			const periodToggleTarget = this.target.find('.toolbar-buttons-set');
			if(periodToggleTarget?.length == 0)
				return

			this.timePeriodRoot = createRoot(periodToggleTarget.get(0));
		}
		const toolbarControls = [WidgetToolboxItem.Info, WidgetToolboxItem.TimeSelector];
		if (this.props.showExportCsv) {
			toolbarControls.splice(1, 0, WidgetToolboxItem.CsvExport);
		}
		this.timePeriodRoot.render(<WidgetToolbox widget={this} controls={toolbarControls} widgetId={this.metricId}
		                                          onCsvExport={this.exportCSV} options={this.props} />)
	}

	// it called from the widget Toolbox component
	preProcessData = (data) => {
		return this.state.legendData
	}

	truncateWindowTitle() {
		if(!this.props.window) {
			const toolbar = $('.metric-table-widget>.toolbar')
			if (toolbar.length) {
				toolbar.css({paddingLeft: '20px'})
			}
			return;
		}

		let timeSelectorPosition = this.target.position().left;
		let titleContainer = this.target.find('.k-window-title');
		titleContainer.css('width', timeSelectorPosition - 30);
		this.target.find('.cw_widget_info_sign').css({right: 55, top: 7});
	}

	hideTableDataWindow () {
		this.doNotTranslatePeriod = false;
		if (this.props.hideWindow) {
			this.window.window.wrapper.find('.k-grid').remove();
			this.props.hideWindow(this.metricId);
		}
		if (this.props.destroyWindow) {
			this.props.destroyWindow(this.props.containerId);
		}
		if (this.props.unsubscribeWindow) {
			this.props.unsubscribeWindow();
		}
		if (this.props.context) {
			this.props.context.hideTableDataWindow(this.props.metricData);
		}
	}

	resizeTableDataWindow() {
		let windowContainer = this.target.find('.k-window-content');
		let windowHeight = windowContainer.height();
		let gridContainer = this.target.find('.k-grid');
		let grid = gridContainer.data('kendoCustomGrid');
		grid.setOptions({
			height: windowHeight - 30
		});
		this.truncateWindowTitle();
	}

	subscribe() {
		if(this.subscription)
			return

		if(!this.state.legendData || !this.state.unitType)
			return

		let subscriptionObj = [{
			eventType: 'Metric',
			releaseEvents: true,
			qualifierId: this.metricId,
			...extractQualifierSubscriptionFields({...this.state.legendData, unitType: this.state.unitType})
		}];
		this.subscription = RemoteEventsManager.subscribeCallback(subscriptionObj, _.throttle(this.onEvents, 2000));
	}

	exportCSV = () => {
		const urlParams = timePeriodToUrl(this.timePeriod);
		const url = `${Settings.serverPath}sessions/${ApplicationState.apiToken}/metrics/${this.metricId}/exportCSV?${urlParams}`;
		Utils.exportGridCsv(this.grid.kendoGrid, url);
	}

	onEvents = events => {
		if(!events?.metric)
			return;

		this.grid?.kendoGrid.dataSource.read();
	}

	onEventsTimeout() {
		this.refreshGridOnEvent();
	}

	refreshGridOnEvent() {
		this.grid?.kendoGrid.dataSource.read();
	}

	removeTimeSelector() {
		this.timePeriodRoot?.unmount();
	}

	destroy () {
		this.removeTimeSelector();
		this.customTimeSelectorContainer?.remove();
	}
}
